/**
 * Who sees an equipment request's RENTAL TERMS — its rate and its files (#571).
 *
 * Mirrors apps/api/src/equipment/rental-terms-access.ts, which is what actually
 * enforces it: the API sends everyone else a null rate and refuses them the
 * files. This copy only decides what the page offers, so a field nobody can
 * fill isn't shown and a save never looks like it is clearing the rate.
 *
 * Whoever can edit equipment requests (`equipment.edit`) plus Admin: equipment
 * managers, the PMs and project engineers. Widened from the EquipmentManager
 * role alone on 2026-10-12. Change both files together.
 */
export const RENTAL_TERMS_PERMISSION = 'equipment.edit';

export function canSeeRentalTerms(
  me: { roles: string[]; permissions: string[] } | null | undefined,
): boolean {
  return !!me && (me.roles.includes('Admin') || me.permissions.includes(RENTAL_TERMS_PERMISSION));
}

/**
 * The `rate` part of the edit drawer's PATCH body.
 *
 * The audience sends what is in the field: a number, or null to clear it.
 *
 * Everyone else sends back EXACTLY the rate the server gave them — never their
 * field, which they can't see. Against this release that is null, which the
 * API ignores from them (EquipmentService.update, pinned by
 * rental-terms.wiring.spec.ts).
 *
 * ⚠️ Echoed rather than omitted, for rollback safety. The pre-#571 API writes
 * `rate = @rate` unconditionally, so an omitted rate reads as "clear it". If
 * the API alone were rolled back while this bundle stayed live, an omitted rate
 * would wipe the real one on every PM's save. That old API also never redacted
 * the rate, so echoing what it sent preserves it. deploy.yml: a change that
 * can't be timed has to be made non-breaking.
 */
export function rateForSave(
  canSee: boolean, raw: string, fromServer: number | null | undefined,
): { rate: number | null } {
  if (!canSee) return { rate: fromServer ?? null };
  return { rate: raw.trim() === '' ? null : parseFloat(raw) };
}
